import { useCallback } from 'react';
import Raven from 'raven-js';
import UsageTracker from 'academy-common-lib/utils/UsageTracker';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { Button } from 'foundations-components/transitional/components/Button';
import { Flex } from 'foundations-components/components/Flex';
import { useCatalogCardModels } from 'academy-common-lib/features/catalog/hooks/useCatalogCardModels';
import { useUpdateUserProgressVisibility } from '../../myLearning/myLearningQueries';
import { SideNavItem } from 'foundations-components/transitional/components/SideNav';
import { useSimplePlaylists } from 'academy-common-lib/features/hubspotPlaylists/hooks/useSimplePlaylists';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { View } from 'foundations-components/components/View';
import { chirpEnumToString } from 'academy-common-lib/utils/chirp';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const truncateContentName = (contentName, maxLength = 75) => {
  if (contentName.length <= maxLength) {
    return contentName;
  }
  return `${contentName.slice(0, maxLength)}...`;
};
export const MyLearningNotification = ({
  contentId,
  contentType,
  contentName
}) => {
  const [setVisibility] = useUpdateUserProgressVisibility({
    contentId,
    contentContainerType: contentType,
    hidden: false
  });
  const {
    isMobile
  } = useMediaQuery();
  const handleUndo = useCallback(() => {
    setVisibility().catch(error => {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyCommon.myLearning.failedToRemoveItem",
          options: {
            contentName
          }
        }),
        type: 'danger',
        timeout: 7000
      });
      Raven.captureException(error);
    });
  }, [contentName, setVisibility]);
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    flexDirection: isMobile ? 'column' : 'row',
    gap: "400",
    inlineSize: "100%",
    justifyContent: "space-between",
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.myLearning.removedFromMyLearning",
      options: {
        contentName
      }
    }), /*#__PURE__*/_jsx(Button, {
      onClick: handleUndo,
      size: "extra-small",
      use: "tertiary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyCommon.myLearning.undo"
      })
    })]
  });
};
const CardMenuMyLearningOption = ({
  item,
  render = content => {
    return /*#__PURE__*/_jsx(SideNavItem, {
      role: "navigation-item",
      title: content
    });
  },
  onClick
}) => {
  var _simplePlaylists$item, _catalogCardModels$;
  const isPlaylistOrLearningPath = item.type === 'PLAYLIST' || item.type === 'LEARNING_PATH';
  const isLiveEvent = item.type === 'LIVE_EVENT';
  const {
    catalogCardModels
  } = useCatalogCardModels({
    items: [item],
    skip: isPlaylistOrLearningPath || isLiveEvent
  });
  const {
    simplePlaylists
  } = useSimplePlaylists({
    playlistIds: [item.id],
    skip: !isPlaylistOrLearningPath
  });
  const playlistName = (simplePlaylists === null || simplePlaylists === void 0 || (_simplePlaylists$item = simplePlaylists[item.id]) === null || _simplePlaylists$item === void 0 ? void 0 : _simplePlaylists$item.name) || '';

  // LIVE_EVENT items come through as LiveEventCardModel which carries `name`,
  // but LearningContentItem doesn't declare it on the prop type. Reading it
  // directly here avoids an extra fetch.
  const liveEventName = isLiveEvent && 'name' in item && typeof item.name === 'string' ? item.name : '';
  const contentName = isLiveEvent ? liveEventName : isPlaylistOrLearningPath ? playlistName : (catalogCardModels === null || catalogCardModels === void 0 || (_catalogCardModels$ = catalogCardModels[0]) === null || _catalogCardModels$ === void 0 ? void 0 : _catalogCardModels$.name) || '';
  const [setVisibility, {
    loading: setVisibilityLoading
  }] = useUpdateUserProgressVisibility({
    contentId: item.id,
    contentContainerType: item.type,
    hidden: true
  });
  const handleOnClick = useCallback(() => {
    onClick === null || onClick === void 0 || onClick();
    if (setVisibilityLoading) return;
    setVisibility().then(() => {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(MyLearningNotification, {
          contentId: item.id,
          contentName: truncateContentName(contentName),
          contentType: item.type
        }),
        type: 'info',
        timeout: 7000
      });
      UsageTracker.trackStandalone('Content Interaction', {
        action: 'remove-from-my-learning',
        contentId: item.id,
        contentType: String(item.type),
        visibility: chirpEnumToString(item.visibility)
      });
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [setVisibilityLoading, item.id, item.type, item.visibility, contentName, onClick, setVisibility]);
  return /*#__PURE__*/_jsx(View, {
    "aria-disabled": setVisibilityLoading,
    as: "div",
    "data-test-id": "remove-from-my-learning-button",
    onClick: handleOnClick,
    children: render( /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.cardMenu.removeFromMyLearning"
    }))
  });
};
export default CardMenuMyLearningOption;